import { computed, inject, Injectable, signal, type Signal } from '@angular/core';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import type {
  CandidateDto,
  JobRequisitionDto,
  RecruitmentStageDto,
} from '../../../../../../core/models/recruitment/recruitment.dto';
import { RecruitmentService } from '../../../../../../core/services/recruitment.service';
import { RecruitmentContextStore } from './recruitment-context.store';
import { resetOnLeavingRecruitment } from './recruitment-scope';

/** Row count the areas already use when they load a list only to feed a lookup or a dialog dropdown. */
const LOOKUP_ROW_COUNT = 100;
/** Stage cache key for the tenant-wide stage list. Not a GUID, so it can never collide with a requisition id. */
const TENANT_WIDE_STAGES_KEY = 'tenant-wide';

/**
 * Light lookup lists several areas' dialogs need: requisitions, pipeline stages (per requisition) and candidates.
 * Each list is fetched once and cached for the Recruitment visit; writers (requisition save, stage save, candidate
 * create) call the matching `invalidate*()` so the next `ensure*()` fetches again. Failed or permission-gated loads
 * leave the previous list (empty at first) and retry on the next `ensure*()`. Everything is dropped when a
 * navigation leaves Recruitment.
 */
@Injectable()
export class RecruitmentReferenceStore {
  private readonly recruitment = inject(RecruitmentService);
  private readonly context = inject(RecruitmentContextStore);

  private readonly requisitionsState = signal<JobRequisitionDto[]>([]);
  private readonly candidatesState = signal<CandidateDto[]>([]);
  private readonly stagesState = signal<ReadonlyMap<string, RecruitmentStageDto[]>>(new Map());

  private requisitionsLoaded = false;
  private candidatesLoaded = false;
  private readonly stagesLoaded = new Set<string>();

  private requisitionsInFlight: Promise<void> | null = null;
  private candidatesInFlight: Promise<void> | null = null;
  private readonly stagesInFlight = new Map<string, Promise<void>>();

  private readonly requisitionsView = this.requisitionsState.asReadonly();
  private readonly candidatesView = this.candidatesState.asReadonly();
  private readonly stageViews = new Map<string, Signal<RecruitmentStageDto[]>>();

  /** Incremented on reset; responses that started in an earlier visit are ignored. */
  private visit = 0;

  constructor() {
    resetOnLeavingRecruitment(() => this.reset());
  }

  requisitions(): Signal<JobRequisitionDto[]> {
    return this.requisitionsView;
  }

  ensureRequisitions(): Promise<void> {
    if (this.requisitionsLoaded) return Promise.resolve();
    if (this.requisitionsInFlight) return this.requisitionsInFlight;
    if (!this.context.canViewRequisitions()) return Promise.resolve();

    const visit = this.visit;
    const request = new Promise<void>((resolve) => {
      this.recruitment
        .jobRequisitions({ skip: 0, rowCount: LOOKUP_ROW_COUNT })
        .pipe(catchError(() => of(null)))
        .subscribe({
          next: (page) => {
            if (visit !== this.visit || page === null) return; // failed load keeps the previous list
            this.requisitionsState.set(page.items ?? []);
            this.requisitionsLoaded = true;
          },
          complete: () => resolve(),
        });
    });
    this.requisitionsInFlight = request;
    void request.then(() => {
      if (this.requisitionsInFlight === request) this.requisitionsInFlight = null;
    });
    return request;
  }

  invalidateRequisitions(): void {
    this.requisitionsLoaded = false;
  }

  /**
   * Stages for one requisition — that requisition's own rows plus the tenant-wide ones — or, for `null`, the
   * tenant-wide stages ONLY. The `null` list is fetched with the server's `tenantWideOnly` flag, so consumers never
   * have to filter requisition-scoped rows out themselves.
   */
  stagesFor(requisitionId: string | null): Signal<RecruitmentStageDto[]> {
    const key = stageKey(requisitionId);
    let view = this.stageViews.get(key);
    if (!view) {
      view = computed(() => this.stagesState().get(key) ?? []);
      this.stageViews.set(key, view);
    }
    return view;
  }

  /** Loads the list `stagesFor(requisitionId)` exposes; `null` loads the tenant-wide stages only. */
  ensureStages(requisitionId: string | null): Promise<void> {
    const key = stageKey(requisitionId);
    if (this.stagesLoaded.has(key)) return Promise.resolve();
    const inFlight = this.stagesInFlight.get(key);
    if (inFlight) return inFlight;

    const visit = this.visit;
    const request = new Promise<void>((resolve) => {
      this.recruitment
        .stages(key, key === TENANT_WIDE_STAGES_KEY)
        .pipe(catchError(() => of(null)))
        .subscribe({
          next: (rows) => {
            if (visit !== this.visit || rows === null) return; // failed load keeps the previous list
            const next = new Map(this.stagesState());
            next.set(key, rows);
            this.stagesState.set(next);
            this.stagesLoaded.add(key);
          },
          complete: () => resolve(),
        });
    });
    this.stagesInFlight.set(key, request);
    void request.then(() => {
      if (this.stagesInFlight.get(key) === request) this.stagesInFlight.delete(key);
    });
    return request;
  }

  /** Stage changes can affect any requisition's list, so every cached stage list is dropped. */
  invalidateStages(): void {
    this.stagesLoaded.clear();
  }

  candidates(): Signal<CandidateDto[]> {
    return this.candidatesView;
  }

  ensureCandidates(): Promise<void> {
    if (this.candidatesLoaded) return Promise.resolve();
    if (this.candidatesInFlight) return this.candidatesInFlight;
    if (!this.context.canViewCandidates()) return Promise.resolve();

    const visit = this.visit;
    const request = new Promise<void>((resolve) => {
      this.recruitment
        .candidates({ skip: 0, rowCount: LOOKUP_ROW_COUNT })
        .pipe(catchError(() => of(null)))
        .subscribe({
          next: (page) => {
            if (visit !== this.visit || page === null) return; // failed load keeps the previous list
            this.candidatesState.set(page.items ?? []);
            this.candidatesLoaded = true;
          },
          complete: () => resolve(),
        });
    });
    this.candidatesInFlight = request;
    void request.then(() => {
      if (this.candidatesInFlight === request) this.candidatesInFlight = null;
    });
    return request;
  }

  invalidateCandidates(): void {
    this.candidatesLoaded = false;
  }

  /** Drops every cached list (the user left Recruitment). */
  reset(): void {
    this.visit++;
    this.requisitionsLoaded = false;
    this.candidatesLoaded = false;
    this.stagesLoaded.clear();
    this.requisitionsInFlight = null;
    this.candidatesInFlight = null;
    this.stagesInFlight.clear();
    this.requisitionsState.set([]);
    this.candidatesState.set([]);
    this.stagesState.set(new Map());
  }
}

function stageKey(requisitionId: string | null): string {
  return requisitionId?.trim() || TENANT_WIDE_STAGES_KEY;
}
